import {
	DISCOUNT_TARGET,
	ILocationDiscount,
	INPSOpportunity,
	IOffer,
	IOpportunity,
	IUpdateProfilePayload,
	IVisitedLocation,
	OrderStore,
	REWARD_FLAG,
	SessionStore,
	mergeAndAdd,
	ICarPass
} from '../../store';
import {IBaseResponse, WebService} from '../../services';
import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Device} from '@capacitor/device';
import {Sorting} from '../../helpers';
import dayjs from 'dayjs';
import {isEqual} from 'lodash';
import {AxiosInstance} from 'axios';

export interface ICustomerProfile {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	birthMonth: number | null;
	birthDay: number | null;
	birthdayModified: number | null;
	picture: string;
	giftCardBalance: number;
	qrCode: string;
	visitedLocations: IVisitedLocation[];
	points: number;
	lifetimePoints: number;
	memberSince: number;
	lastVisit: number | null;
	opportunities: IOpportunity[];
	npsOpportunities: INPSOpportunity[];
	offers: IOffer[];
	usedOffers: IOffer[];
	dataPrivate: boolean;
	receiveOffers: boolean;
	acceptedTOS: boolean;
	receivedSignupReward: boolean;
	receivedAppInstallReward: boolean;
	pushNotificationsEnabled: boolean;
	trackUrl: string | null;
	trackCode: string | null;
}

export type TMaybeOffer = IOffer | null;

interface IPushNotificationPayload {
	token: string | null;
	appId: string;
	appVersion: string;
	manufacturer: string;
	model: string;
	name: string;
	os: string;
	osVersion: string;
	platform: string;
}

export const DEFAULT_NAMES = [
	'new',
	'customer',
];

@Module({name: 'customerProfile', namespaced: true})
class CustomerProfileModule extends VuexModule {
	customerProfile: ICustomerProfile | null = null;
	pushToken: string | null = null;
	lastSentPushNotificationPayload: IPushNotificationPayload | null = null;
	carPasses: ICarPass[] = [];
	useDefaultLogo = false;
	skipPromptName = false;

	get trimmedFirstName() {
		if(this.customerProfile && this.customerProfile.firstName) {
			return this.customerProfile?.firstName.substring(0, 20);
		}
		return '';
	}

	get trimmedLastName() {
		if(this.customerProfile && this.customerProfile.lastName) {
			return this.customerProfile?.lastName.substring(0, 20);
		}
		return '';
	}

	get convertOfferToLocationDiscount() {
		return (offer: IOffer): ILocationDiscount => {
			return {
				allowedItems: [],
				description: offer.description,
				discountId: null,
				flags: offer.flags,
				name: offer.name,
				offerId: Number(offer.id),
				priceDelta: 0,
				target: offer.target
			};
		};
	}

	get unusedOffersForCart(): IOffer[] {
		const cartItemIds = OrderStore.itemAndItemHqIdsWithoutDiscounts;
		if(cartItemIds.length === 0) {
			return [];
		}
		return this.unusedOffersForOrdering.filter((offer) => {
			return offer.items.length === 0 || offer.items.some((item) => {
				return cartItemIds.includes(item.itemId);
			});
		});
	}

	get offerValidForItem() {
		return (offer: IOffer, itemHqId: string | null, itemId: string | null): boolean => {
			// If there are no item restrictions or if the item allowed for the offer, it's usable
			// Else we need to see if the itemId on the item matches either the HQ or LC id on the offer
			// In almost all cases it will be the itemHqId but if the discount was created at the LC-level
			// then it will be the itemId
			return offer.items.length === 0 || offer.items.some((item) => {
				return item.itemId === itemHqId || item.itemId === itemId;
			});
		};
	}

	get offersForItem() {
		return (itemHqId: string | null, itemId: string | null): IOffer[] => {
			return this.unusedOffersForOrdering.filter((offer) => {
				return this.offerValidForItem(offer, itemHqId, itemId);
			});
		};
	}

	get offers(): IOffer[] {
		if(this.customerProfile && this.customerProfile.offers) {
			// If we have visit rewards we need to add them in
			const brandConfig = SessionStore.brandConfig;
			let rewardOffers: IOffer[] = [];
			if(brandConfig) {
				let maxOfferId = 0;
				if(this.customerProfile.offers.length) {
					maxOfferId = Math.max(...this.customerProfile.offers.map((offer) => offer.id));
				}
				rewardOffers = (new Array(this.earnedLoyaltyRewards)).fill(0).map((_, index) => {
					return {
						id: maxOfferId + 1000 + index,
						name: brandConfig.loyaltyPointsRewardName,
						description: 'Your points reward for being a loyal customer.',
						image: brandConfig.rewardImage,
						expires: null,
						locations: null,
						target: DISCOUNT_TARGET.ITEM,
						items: [],
						flags: REWARD_FLAG.VISITS,
						modDate: null
					};
				});
			}

			return [
				...this.customerProfile.offers,
				...rewardOffers
			];
		}
		return [];
	}

	get redeemedOffers(): IOffer[] {
		return this.customerProfile ? this.customerProfile.usedOffers : [];
	}

	get offersForOrdering(): IOffer[] {
		return this.offers.filter((offer) => {
			// If it's an order discount, it's not usable currently
			if(offer.target === DISCOUNT_TARGET.ORDER) {
				return false;
			}

			// Check if the offer has any option/choice restrictions
			for(const item of offer.items) {
				if(item.optionId !== null || item.choiceIds.length > 0) {
					return false;
				}
			}

			// We got to the end so this offer is usable
			return true;
		});
	}

	get earnedLoyaltyRewards() {
		if(!this.customerProfile || !SessionStore.brandConfig) {
			return 0;
		}
		return Math.floor(this.customerProfile.points / SessionStore.brandConfig.loyaltyPointsPerReward);
	}

	get currentRewardPoints(): number {
		if(!this.customerProfile || !SessionStore.brandConfig) {
			return 0;
		}
		const points = this.customerProfile.points % SessionStore.brandConfig.loyaltyPointsPerReward;
		return Math.floor(points);
	}

	get totalRewardPoints(): number {
		if(!this.customerProfile) {
			return 0;
		}
		return Math.floor(this.customerProfile.points);
	}

	get unusedOffersForOrdering() {
		// For now, we only support one offer per order
		if(OrderStore.orderHasAnyDiscounts) {
			return [];
		}
		const workingTicket = OrderStore.workingTicket;
		const usedOfferIds: number[] = [];
		let usedVisitRewards = 0;
		if(workingTicket) {
			for(const lineItem of workingTicket.lineItems) {
				for(const discount of lineItem.discounts) {
					if((discount.flags & REWARD_FLAG.VISITS) === REWARD_FLAG.VISITS) {
						usedVisitRewards++;
					} else if(discount.offerId) {
						usedOfferIds.push(discount.offerId);
					} else {
						console.warn('Discount has no offerId and is not a visits reward', discount);
					}
				}
			}
			return this.offersForOrdering.filter((offer) => {
				if((offer.flags & REWARD_FLAG.VISITS) === REWARD_FLAG.VISITS) {
					if(usedVisitRewards > 0) {
						usedVisitRewards--;
						return false;
					}
				} else if(usedOfferIds.includes(offer.id)) {
					return false;
				}

				return true;
			});
		}
		return this.offersForOrdering;
	}


	get hasUnclaimedOpportunities() {
		if(this.customerProfile && this.customerProfile.opportunities) {
			return this.customerProfile.opportunities.length > 0;
		}
		return false;
	}

	get unclaimedOpportunityId() {
		if(this.customerProfile && this.customerProfile.opportunities && this.customerProfile.opportunities.length > 0) {
			return this.customerProfile.opportunities[this.customerProfile.opportunities.length - 1].id;
		}
		return null;
	}

	get opportunity() {
		return (id: string) => {
			if(!this.customerProfile || !this.customerProfile.opportunities || this.customerProfile.opportunities.length === 0) {
				return null;
			}
			return this.customerProfile.opportunities.find((opportunity) => {
				return opportunity.id === id;
			}) || null;
		};
	}

	/**
	 * Returns non-expired opportunities.
	 */
	get opportunities() {
		return this.customerProfile?.opportunities ?? [];
	}

	get newestNonExpiredOpportunity() {
		let newest: IOpportunity | null = null;
		this.opportunities.forEach((opportunity) => {
			if((newest === null || opportunity.created > newest.created) && dayjs.unix(opportunity.expires).isAfter(dayjs())) {
				newest = opportunity;
			}
		});
		return newest;
	}

	get hasUnclaimedNPSOpportunities() {
		if(this.customerProfile && this.customerProfile.npsOpportunities) {
			return this.customerProfile.npsOpportunities.length > 0;
		}
		return false;
	}

	get unclaimedNPSOpportunityId() {
		if(this.customerProfile && this.customerProfile.npsOpportunities && this.customerProfile.npsOpportunities.length > 0) {
			return this.customerProfile.npsOpportunities[this.customerProfile.npsOpportunities.length - 1].id;
		}
		return null;
	}

	get hasProfilePic(): boolean {
		return !!this.customerProfile &&
			!!this.customerProfile.picture &&
			this.customerProfile.picture.indexOf('no-profile') === -1;
	}

	get useProfilePic() {
		if(!this.useDefaultLogo) {
			return this.hasProfilePic;
		} else {
			return false;
		}
	}

	get nps() {
		return (id: string) => {
			if(!this.customerProfile || !this.customerProfile.npsOpportunities || this.customerProfile.npsOpportunities.length === 0) {
				return null;
			}
			return this.customerProfile.npsOpportunities.find((opportunity) => {
				return opportunity.id === id;
			}) || null;
		};
	}

	get carPassFromCode() {
		return (code: string) => {
			return this.carPasses.find(
				(pass) => {
					return pass.code === code;
				}
			) ?? null;
		};
	}

	/**
	 * returns a copy of customerProfile.visitedLocations sorted by lastVisit
	 */
	get visitedLocationsByVisitDate() {
		const locations = this.customerProfile?.visitedLocations ?? [];
		return [...locations].sort(Sorting.sortVisitedLocationsByMostRecent);
	}

	get trackingClient(): null | AxiosInstance {
		if(
			!SessionStore.brandConfig ||
			!this.customerProfile ||
			!this.customerProfile.trackUrl ||
			!this.customerProfile.trackCode
		) {
			return null;
		}
		return WebService.getTrackingClient(
			SessionStore.brandConfig.id,
			this.customerProfile.id,
			this.customerProfile.trackUrl,
			this.customerProfile.trackCode
		);
	}

	@Action
	async getPushNotificationPayload(): Promise<IPushNotificationPayload> {
		const info = await Device.getInfo();
		return {
			token: this.pushToken || null,
			appId: await WebService.getDomain(),
			appVersion: process.env.VUE_APP_VERSION as string,
			manufacturer: info.manufacturer,
			model: info.model,
			name: info.name || '',
			os: info.operatingSystem,
			osVersion: info.osVersion,
			platform: info.platform
		};
	}

	@Action
	async fetchProfile() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{profile: ICustomerProfile}>>('profile');
		this.setCustomerProfile(response.data.data.profile);
	}

	@Action
	async updateProfile(payload: Partial<IUpdateProfilePayload>) {
		if(!this.customerProfile) {
			console.error('Cannot update profile without a customer profile');
			return;
		}
		const response = await WebService.authenticatedClient.post<IBaseResponse<{profile: ICustomerProfile}>>('profile', {
			...this.customerProfile,
			...payload
		});
		this.setCustomerProfile(response.data.data.profile);
	}

	@Action
	async updateProfilePicture(picture: File) {
		const formData = new FormData();
		formData.append('picture', picture, picture.name);
		const response = await WebService.authenticatedClient.post<IBaseResponse<{profile: ICustomerProfile}>>('profile/picture', formData);
		this.setCustomerProfile(response.data.data.profile);
	}

	@Action
	async updatePushNotificationToken(payload: {token: string | null; force?: boolean}) {
		const force = payload.force || false;
		// Only send this up if we have a change or if we are forcing it
		this.setPushToken(payload.token);
		const updatePayload = await this.getPushNotificationPayload();
		if(force || !isEqual(updatePayload, this.lastSentPushNotificationPayload)) {
			const response = await WebService.authenticatedClient.post<IBaseResponse<{profile: ICustomerProfile}>>('profile/push_notifications', updatePayload);
			// If the call went through then we can update the last seen token
			this.setLastSentPushNotificationPayload(updatePayload);
			this.setCustomerProfile(response.data.data.profile);
		}
	}

	@Action
	async acceptTOS(signupCampaignId: string | null = null) {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{profile: ICustomerProfile}>>('accept_tos', {
			campaignId: signupCampaignId
		});
		this.setCustomerProfile(response.data.data.profile);
	}

	@Action
	async getCarPass(params: {code: string; force?: boolean}) {
		if(params.force || !this.carPassFromCode(params.code)) {
			const response = await WebService.authenticatedClient.get<IBaseResponse<{carPass: ICarPass}>>(`car_passes/${params.code}`, {});
			this.addCarPass(response.data.data.carPass);
		}
	}

	@Action
	async deleteCarPass(code: string) {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{deleted: string}>>(`car_passes/${code}/delete`, {});
		this.removeCarPass(response.data.data.deleted);
		return response.data.data.deleted;
	}

	@Action
	async deleteProfilePic() {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{profile: ICustomerProfile}>>('profile/picture', {
			delete: true
		});
		this.setCustomerProfile(response.data.data.profile);
		this.setUseDefaultLogo(true);
	}

	@Mutation
	public setCustomerProfile(customerProfile: ICustomerProfile) {
		this.customerProfile = customerProfile;
	}

	@Mutation
	public setPushToken(token: string | null) {
		this.pushToken = token;
	}

	@Mutation
	public clearCustomerProfile() {
		this.customerProfile = null;
		this.lastSentPushNotificationPayload = null;
		this.pushToken = null;
	}

	@Mutation
	public setLastSentPushNotificationPayload(payload: IPushNotificationPayload) {
		this.lastSentPushNotificationPayload = payload;
	}

	@Mutation
	public addCarPass(carPass: ICarPass) {
		mergeAndAdd(this.carPasses, carPass, ['code']);
	}

	@Mutation
	public removeCarPass(code: string) {
		const index = this.carPasses.findIndex(
			(pass) => {
				return pass.code === code;
			}
		);
		if(index !== -1) {
			this.carPasses.splice(index, 1);
		}
	}

	@Mutation
	public removeExpiredOpportunities() {
		if(this.customerProfile && this.customerProfile.opportunities) {
			const nonExpired = this.customerProfile.opportunities.filter(
				(opportunity) => {
					return dayjs.unix(opportunity.expires).isAfter(dayjs());
				}
			);
			this.customerProfile.opportunities = nonExpired;
		}
	}

	@Mutation
	public setUseDefaultLogo(value: boolean) {
		this.useDefaultLogo = value;
	}

	@Mutation
	public setSkipPromptName(value: boolean) {
		this.skipPromptName = value;
	}

	@Mutation
	public addOffer(offer: IOffer) {
		if(this.customerProfile) {
			mergeAndAdd(this.customerProfile.offers, [offer], ['id']);
		} else {
			console.warn('Failed to add offer to store: customerProfile is not set');
		}
	}
}

export {
	CustomerProfileModule
};
